import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight, Trophy, Target, Users, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.includes('/api/files/')) {
    const path = url.substring(url.indexOf('/api/files/'));
    return `${BACKEND_URL}${path}`;
  }
  return url;
};

const typeIcons = {
  mvp: Trophy,
  artilheiro: Target,
  garcom: Users,
  relogio: Clock
};

const typeColors = {
  mvp: 'from-yellow-400 to-amber-600',
  artilheiro: 'from-green-400 to-emerald-600',
  garcom: 'from-blue-400 to-cyan-600',
  relogio: 'from-purple-400 to-violet-600'
};

const HighlightCarousel = ({ highlights, collectivePhotoUrl }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  const allSlides = [
    ...highlights,
    { type: 'collective', title: 'O TIME', imageUrl: collectivePhotoUrl || 'https://images.pexels.com/photos/3319180/pexels-photo-3319180.jpeg' }
  ];

  return (
    <div className="relative w-full">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {allSlides.map((slide, index) => {
            if (slide.type === 'collective') {
              return (
                <div key="collective" className="flex-none w-[85%] min-w-0 pl-4 first:pl-0">
                  <div className="relative h-80 sm:h-96 rounded-2xl overflow-hidden border-4 border-zinc-950">
                    <img
                      src={slide.imageUrl}
                      alt="O Time"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="font-bebas text-4xl tracking-tight">FUTEBOL DE DOMINGO</h3>
                      <p className="text-sm opacity-80">O melhor do futebol amador</p>
                    </div>
                  </div>
                </div>
              );
            }

            const Icon = typeIcons[slide.type] || Trophy;
            const gradientClass = typeColors[slide.type] || 'from-zinc-700 to-zinc-900';
            const player = slide.player;
            const highlightUrl = getImageUrl(player.highlight_image_url);
            const profileUrl = getImageUrl(player.profile_image_url);

            return (
              <div key={slide.type} className="flex-none w-[85%] min-w-0 pl-4 first:pl-0">
                <div className={`relative h-80 sm:h-96 rounded-2xl overflow-hidden bg-gradient-to-br ${gradientClass} border-2 border-zinc-800`}>
                  {/* Background image - uses highlight photo if available */}
                  {(highlightUrl || profileUrl) && (
                    <div className="absolute inset-0">
                      <img
                        src={highlightUrl || profileUrl}
                        alt={player.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  
                  {/* Content */}
                  <div className="relative h-full flex flex-col justify-between p-6 text-white">
                    {/* Badge */}
                    <div className="flex items-center gap-2">
                      <span className="bg-yellow-400 text-zinc-950 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                        {slide.badge}
                      </span>
                      <Icon size={20} className="text-yellow-400" />
                    </div>
                    
                    {/* Player info */}
                    <div className="flex items-end gap-4">
                      {/* Player profile photo (small) */}
                      {profileUrl && (
                        <div className="flex-shrink-0">
                          <img
                            src={profileUrl}
                            alt={player.name}
                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-4 border-white/30"
                          />
                        </div>
                      )}
                      
                      {/* Stats */}
                      <div className="flex-1 mb-1">
                        <h3 className="font-bebas text-3xl sm:text-4xl tracking-tight uppercase leading-none drop-shadow-lg">
                          {player.name}
                        </h3>
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1 bg-black/30 px-2 py-1 rounded">
                            <span className="text-yellow-400">🏅</span>
                            {player.pontos_rating?.toFixed(1) || '0.0'}
                          </span>
                          <span className="flex items-center gap-1 bg-black/30 px-2 py-1 rounded">
                            <span className="text-green-400">⚽</span>
                            {player.goals || 0}
                          </span>
                          <span className="flex items-center gap-1 bg-black/30 px-2 py-1 rounded">
                            <span className="text-blue-400">🎯</span>
                            {player.assists || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        onClick={scrollPrev}
        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border-2 border-zinc-950 flex items-center justify-center hover:bg-zinc-100 transition-colors z-10"
        data-testid="carousel-prev"
      >
        <ChevronLeft size={24} strokeWidth={3} />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border-2 border-zinc-950 flex items-center justify-center hover:bg-zinc-100 transition-colors z-10"
        data-testid="carousel-next"
      >
        <ChevronRight size={24} strokeWidth={3} />
      </button>

      {/* Dots indicator */}
      <div className="flex justify-center gap-2 mt-4">
        {allSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => emblaApi?.scrollTo(index)}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === selectedIndex ? 'bg-green-500' : 'bg-zinc-300'
            }`}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HighlightCarousel;
